import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ShoppingCartIcon,
    ChevronUpIcon,
    TicketIcon,
    ChatBubbleLeftRightIcon,
    XMarkIcon,
    HeartIcon,
    PhoneIcon
} from '@heroicons/react/24/outline';
import { useNavigation } from '../../contexts/NavigationContext';
import { useNavigate } from 'react-router-dom';
import { useAssistant } from '../../contexts/AssistantContext';
import { useSafariBuilder } from '../../contexts/SafariBuilderContext';
import { useGiftCart } from '../../contexts/GiftCartContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useToast } from '../../contexts/ToastContext';
import logoImage from '../../assets/logo.png';
import GiftCartModal from '../GiftShop/GiftCartModal';
import GiftCheckoutModal from '../GiftShop/GiftCheckoutModal';

const FloatingHub: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [showScrollTop, setShowScrollTop] = useState(false);
    const { actualTheme } = useTheme();
    const { minimize, maximize, mode: assistantMode } = useAssistant();
    const { openBuilder } = useSafariBuilder();
    const { 
        totalItems: giftCartCount, 
        isCartOpen, setIsCartOpen, 
        isCheckoutOpen, setIsCheckoutOpen 
    } = useGiftCart();
    const { showInfo } = useToast();
    const { counts } = useNavigation();
    const navigate = useNavigate();
    const [safariCartCount, setSafariCartCount] = useState(0);

    // Sum up all notification counts
    const totalCount = (counts.wishlist || 0) + (safariCartCount || 0) + (giftCartCount || 0);

    // Fetch Safari Builder Cart Count (matching SafariBuilderButton logic)
    useEffect(() => {
        const updateCount = async () => {
            const saved = localStorage.getItem('customSafariCart');
            if (saved) {
                try {
                    const items = JSON.parse(saved);
                    setSafariCartCount(items.length);
                } catch (e) { console.error(e); }
            }
        };
        updateCount();
        window.addEventListener('storage', updateCount);
        window.addEventListener('cartUpdated', updateCount);
        return () => {
            window.removeEventListener('storage', updateCount);
            window.removeEventListener('cartUpdated', updateCount);
        };
    }, []);

    const isDark = actualTheme === 'dark';

    // Handle scroll for Back to Top visibility and auto-close hub
    useEffect(() => {
        const handleScroll = () => {
            setShowScrollTop(window.scrollY > 400);
            if (isOpen) setIsOpen(false); // Auto-close on scroll
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [isOpen]);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setIsOpen(false);
    };

    const handleWishlist = () => {
        navigate('/wishlist');
        setIsOpen(false);
    };

    const handleSupport = () => {
        navigate('/contact');
        setIsOpen(false);
    };

    const handleGiftCheckout = () => {
        setIsCartOpen(true);
        setIsOpen(false);
    };

    const handleFinalCheckout = () => {
        setIsCartOpen(false);
        setIsCheckoutOpen(true);
    };

    const toggleHub = () => setIsOpen(!isOpen);

    // Animation Variants
    const menuVariants = {
        closed: { opacity: 0, scale: 0.8, y: 20 },
        open: {
            opacity: 1,
            scale: 1,
            y: 0,
            transition: {
                type: 'spring',
                stiffness: 300,
                damping: 25,
                staggerChildren: 0.1,
                delayChildren: 0.1
            }
        }
    } as const;

    const itemVariants = {
        closed: { opacity: 0, x: 20, scale: 0.8 },
        open: { opacity: 1, x: 0, scale: 1 }
    } as const;

    const hubBg = isDark ? 'bg-slate-900/95 border-slate-700' : 'bg-white/95 border-gray-200';
    const hubText = isDark ? 'text-white' : 'text-slate-900';

    return (
        <div className="fixed bottom-8 right-8 z-[101] flex flex-col items-end gap-4">
            {/* Gift Cart & Checkout Modals */}
            <GiftCartModal 
                isOpen={isCartOpen} 
                onClose={() => setIsCartOpen(false)} 
                onCheckout={handleFinalCheckout} 
            />
            <GiftCheckoutModal
                isOpen={isCheckoutOpen}
                onClose={() => setIsCheckoutOpen(false)}
            />

            {/* Backdrop for click-outside closure */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setIsOpen(false)}
                        className="fixed inset-0 z-[-1] bg-slate-900/5 backdrop-blur-[2px] pointer-events-auto"
                    />
                )}
            </AnimatePresence>


            {/* Expanded Menu */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        variants={menuVariants}
                        initial="closed"
                        animate="open"
                        exit="closed"
                        className="flex flex-col items-end gap-3 pointer-events-auto"
                    >
                        {/* 1. My Wishlist */}
                        <motion.button
                            variants={itemVariants}
                            onClick={handleWishlist}
                            className={`flex items-center gap-3 px-5 py-3 rounded-full shadow-lg backdrop-blur-md border ${hubBg} ${hubText} hover:scale-105 transition-transform group/item`}
                        >
                            <span className="text-[10px] sm:text-xs font-bold transition-opacity whitespace-nowrap">My Wishlist</span>
                            <div className="relative">
                                <HeartIcon className="w-5 h-5 text-red-500" />
                                {counts.wishlist > 0 && (
                                    <span className="absolute -top-2 -right-2 bg-red-600 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">
                                        {counts.wishlist}
                                    </span>
                                )}
                            </div>
                        </motion.button>

                        {/* 2. Custom Safari Builder */}
                        <motion.button
                            variants={itemVariants}
                            onClick={() => { openBuilder(); setIsOpen(false); }}
                            className={`flex items-center gap-3 px-5 py-3 rounded-full shadow-lg backdrop-blur-md border ${hubBg} ${hubText} hover:scale-105 transition-transform group/item`}
                        >
                            <span className="text-[10px] sm:text-xs font-bold transition-opacity whitespace-nowrap">Custom Safari</span>
                            <div className="relative">
                                <TicketIcon className="w-5 h-5 text-primary-500" />
                                {safariCartCount > 0 && (
                                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">
                                        {safariCartCount}
                                    </span>
                                )}
                            </div>
                        </motion.button>

                        {/* 4. Safari Assistant */}
                        <motion.button
                            variants={itemVariants}
                            onClick={() => {
                                if (assistantMode === 'minimized') maximize();
                                else minimize();
                                setIsOpen(false);
                            }}
                            className={`flex items-center gap-3 px-5 py-3 rounded-full shadow-lg backdrop-blur-md border ${hubBg} ${hubText} hover:scale-105 transition-transform group/item`}
                        >
                            <span className="text-[10px] sm:text-xs font-bold transition-opacity whitespace-nowrap">Assistant</span>
                            <ChatBubbleLeftRightIcon className="w-5 h-5 text-primary-500" />
                        </motion.button>

                        {/* 5. Gift Checkout */}
                        {giftCartCount > 0 && (
                            <motion.button
                                variants={itemVariants}
                                onClick={handleGiftCheckout}
                                className={`flex items-center gap-3 px-5 py-3 rounded-full shadow-lg backdrop-blur-md border ${hubBg} ${hubText} hover:scale-105 transition-transform group/item`}
                            >
                                <span className="text-[10px] sm:text-xs font-bold transition-opacity whitespace-nowrap">Gift Shop</span>
                                <div className="relative">
                                    <ShoppingCartIcon className="w-5 h-5 text-blue-500" />
                                    <span className="absolute -top-2 -right-2 bg-blue-600 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">
                                        {giftCartCount}
                                    </span>
                                </div>
                            </motion.button>
                        )}

                        {/* 6. Contact Support */}
                        <motion.button
                            variants={itemVariants}
                            onClick={handleSupport}
                            className={`flex items-center gap-3 px-5 py-3 rounded-full shadow-lg backdrop-blur-md border ${hubBg} ${hubText} hover:scale-105 transition-transform group/item`}
                        >
                            <span className="text-[10px] sm:text-xs font-bold transition-opacity whitespace-nowrap">Support</span>
                            <PhoneIcon className="w-5 h-5 text-emerald-500" />
                        </motion.button>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Main Toggle Button */}
            <motion.button
                onClick={toggleHub}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`w-16 h-16 rounded-full shadow-2xl flex items-center justify-center backdrop-blur-xl border border-white/30 pointer-events-auto bg-[#2A7ECE] text-white relative group overflow-visible`}
            >
                <AnimatePresence mode="wait">
                    {isOpen ? (
                        <motion.div
                            key="close"
                            initial={{ scale: 0, rotate: -90 }}
                            animate={{ scale: 1, rotate: 0 }}
                            exit={{ scale: 0, rotate: 90 }}
                        >
                            <XMarkIcon className="w-8 h-8" />
                        </motion.div>
                    ) : (
                        <motion.div
                            key="open"
                            initial={{ scale: 0, rotate: 90 }}
                            animate={{ scale: 1, rotate: 0 }}
                            exit={{ scale: 0, rotate: -90 }}
                            className="flex flex-col items-center justify-center relative"
                        >
                             {/* Pulsing Logo */}
                             <motion.div
                                animate={{
                                    scale: [1, 1.1, 1],
                                    opacity: [0.9, 1, 0.9]
                                }}
                                transition={{
                                    duration: 2,
                                    repeat: Infinity,
                                    ease: "easeInOut"
                                }}
                                className="w-10 h-10 flex items-center justify-center rounded-full overflow-hidden bg-white/20 p-1"
                             >
                                <img src={logoImage} alt="SightTrails" className="w-full h-full object-contain brightness-0 invert" />
                             </motion.div>

                             {/* Unified Total Count Indicator */}
                             {totalCount > 0 && (
                                 <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    className="absolute -top-3 -right-3 min-w-[24px] h-[24px] px-1.5 bg-red-600 border-2 border-white text-white text-[11px] font-black rounded-full flex items-center justify-center shadow-lg"
                                 >
                                     {totalCount}
                                 </motion.div>
                             )}
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.button>
        </div>
    );
};

export default FloatingHub;
